import type { BadgeTone } from '@shared/ui';

import { FlagType } from './flag';

/**
 * How a flag type renders, which tab it lives on, and a client-side mirror of
 * the Salesforce target-field rule.
 *
 * The rule half follows `flag-key.ts`: the API is the authority, and this copy
 * exists so the create form can refuse `Gift Aid` while the operator is still
 * typing rather than after they have reviewed and confirmed. A field name that
 * gets past the API and does not exist in the org fails at publish time, as a
 * DLQ entry — so this copy must never be laxer than the API's.
 *
 * @see apps/api/src/modules/feature-flags/domain/flag-type.ts — the authority
 */

export const FLAG_TYPE_LABEL: Record<FlagType, string> = {
    veevart2: 'Veevart 2',
    salesforce: 'Salesforce',
};

export const FLAG_TYPE_TONE: Record<FlagType, BadgeTone> = {
    veevart2: 'neutral',
    salesforce: 'brand',
};

/** One sentence each, for the create form's type choice. */
export const FLAG_TYPE_DESCRIPTION: Record<FlagType, string> = {
    veevart2: 'Read by Veevart 2 services. Never leaves the platform.',
    salesforce:
        "Read inside each customer's Salesforce org, as a checkbox on V2_Feature_Flags__c. On or off per org — no rollout percentage.",
};

/** Tab order on the flags list. `veevart2` first: every flag that predates the split is one. */
export const FLAG_TYPE_ORDER: readonly FlagType[] = ['veevart2', 'salesforce'];

/** The search param the flags list keeps its tab in, so a link opens the right one. */
export const FLAG_TYPE_PARAM = 'type';

/**
 * Anything unrecognised — including no param at all — reads as `veevart2`, so
 * every `/flags` link written before the tabs existed still opens the list it
 * used to.
 */
export function flagTypeFromParam(value: string | null): FlagType {
    const parsed = FlagType.safeParse(value);
    return parsed.success ? parsed.data : 'veevart2';
}

/* ------------------------------------------------------ salesforce field - */

/** Mirrors the API's `SALESFORCE_FIELD_PATTERN`: a custom-field API name, e.g. `Checkout_New_Flow__c`. */
export const SALESFORCE_FIELD_PATTERN = /^[A-Za-z][A-Za-z0-9_]*__c$/;

/** Mirrors the API's `SALESFORCE_FIELD_MAX_LENGTH`: Salesforce's own cap, 40 plus `__c`. */
export const SALESFORCE_FIELD_MAX_LENGTH = 43;

/** Mirrors the API's `isSalesforceFieldName` exactly — `flag-type.test.ts` pins the agreement. */
export function isSalesforceFieldName(candidate: string): boolean {
    return (
        candidate.length <= SALESFORCE_FIELD_MAX_LENGTH && SALESFORCE_FIELD_PATTERN.test(candidate)
    );
}

export type SalesforceFieldRejection = 'empty' | 'too-long' | 'malformed';

/**
 * Classify a candidate target field. Returns `null` when it is valid.
 *
 * Trimmed first, because the API's request schema trims before it applies
 * {@link isSalesforceFieldName} — a trailing space the API would accept must
 * not be refused here.
 */
export function rejectSalesforceField(candidate: string): SalesforceFieldRejection | null {
    const value = candidate.trim();

    if (value.length === 0) return 'empty';
    if (value.length > SALESFORCE_FIELD_MAX_LENGTH) return 'too-long';
    return isSalesforceFieldName(value) ? null : 'malformed';
}

export function explainSalesforceFieldRejection(rejection: SalesforceFieldRejection): string {
    switch (rejection) {
        case 'empty':
            return 'A Salesforce flag needs the field it is projected to.';
        case 'too-long':
            return `Salesforce caps a custom-field API name at ${String(SALESFORCE_FIELD_MAX_LENGTH)} characters, \`__c\` included.`;
        case 'malformed':
            return 'Use a Salesforce custom-field API name: letters, digits and underscores after a leading letter, ending in `__c` — e.g. `Checkout_New_Flow__c`.';
    }
}

/* ------------------------------------------------------------ kind copy - */

/** Enough of a definition to say where a flag's value goes. */
export interface FlagKind {
    readonly type: FlagType;
    readonly salesforceField?: string | undefined;
}

/**
 * The sentence every write confirmation carries (ADR 0009 §5).
 *
 * The two kinds reach different systems — one a Veevart 2 service, the other a
 * customer's Salesforce org — and an operator confirming a change should not
 * have to remember which one this key is.
 */
export function flagKindReach(kind: FlagKind): string {
    if (kind.type === 'salesforce') {
        return `This is a Salesforce flag: the value is projected into customers' Salesforce orgs as ${salesforceTarget(kind)}.`;
    }
    return 'This is a Veevart 2 flag: only Veevart 2 services read it, and nothing is sent to Salesforce.';
}

/** `the Gift_Aid__c field on V2_Feature_Flags__c`, without a blank where a field should be. */
export function salesforceTarget(kind: FlagKind): string {
    return kind.salesforceField !== undefined
        ? `the ${kind.salesforceField} field on V2_Feature_Flags__c`
        : 'its field on V2_Feature_Flags__c';
}
